import { useEffect, useId, useRef, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { cn } from '../../utils/utils';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  showCloseButton?: boolean;
  closeOnBackdropClick?: boolean;
  closeOnEscape?: boolean;
  className?: string;
  overlayClassName?: string;
  contentClassName?: string;
  /** Accessible label when the modal has no visible `title`. Either
   *  this or `title` should be set so screen readers can announce the
   *  dialog purpose. */
  ariaLabel?: string;
}

/**
 * Move keyboard focus inside `container` and return a cleanup that
 * restores focus to whatever was focused before. Picks the first
 * "natural" focus target - explicit `data-autofocus`, then the close
 * button, then any focusable element. No-op when no candidate exists.
 */
function focusFirstWithin(container: HTMLElement | null): void {
  if (!container) return;
  const explicit = container.querySelector<HTMLElement>('[data-autofocus]');
  if (explicit) {
    explicit.focus();
    return;
  }
  const first = container.querySelector<HTMLElement>(
    'button, [href], input:not([disabled]), select:not([disabled]), ' +
      'textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
  );
  if (first) first.focus();
}

const sizeClasses = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
  full: 'max-w-[95vw] max-h-[95vh]',
};

export function Modal({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  showCloseButton = true,
  closeOnBackdropClick = true,
  closeOnEscape = true,
  className,
  overlayClassName,
  contentClassName,
  ariaLabel,
}: ModalProps) {
  const titleId = useId();
  const contentRef = useRef<HTMLDivElement | null>(null);

  // Handle escape key and body scroll
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (closeOnEscape && e.key === 'Escape') {
        onClose();
      }
    };

    // Prevent body scroll when modal is open
    document.body.style.overflow = 'hidden';

    if (closeOnEscape) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.body.style.overflow = 'unset';
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose, closeOnEscape]);

  // Focus management: when the dialog opens, move focus inside. When it
  // closes, restore focus to whatever opened it. This is the minimum
  // a11y contract for a modal - without it, keyboard users get
  // stranded (Tab can wander back to elements behind the overlay) and
  // screen-reader users hear nothing when the dialog appears.
  useEffect(() => {
    if (!isOpen) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    // requestAnimationFrame so the portal has mounted before we focus.
    const raf = window.requestAnimationFrame(() => {
      focusFirstWithin(contentRef.current);
    });
    const content = contentRef.current;
    return () => {
      window.cancelAnimationFrame(raf);
      // Restore focus if it was still in the (now-closing) modal. By the time
      // this cleanup runs React has usually removed the dialog already, and a
      // focused node that leaves the DOM drops focus to <body> - so <body>
      // counts as "still in the modal". Checking `contains` alone never
      // matched, and every closed modal stranded keyboard users at the top of
      // the page. If the user clicked something outside (which closed the
      // modal), focus is on that instead, and is left there.
      const active = document.activeElement;
      const stillInModal =
        active === null ||
        active === document.body ||
        (content?.contains(active) ?? false);
      if (previouslyFocused && stillInModal) {
        previouslyFocused.focus?.();
      }
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (closeOnBackdropClick && e.target === e.currentTarget) {
      onClose();
    }
  };

  // Render via portal into <body> so the modal escapes any ancestor's
  // containing block. Without this, `position: fixed` gets trapped
  // inside ancestors that set `backdrop-filter`, `transform`, `filter`,
  // or `will-change` (CSS spec) - which is what was happening when the
  // modal opened from inside SourcesPanel (its wrapper uses
  // `backdrop-blur-sm`, creating a containing block). Portal hoists the
  // overlay out of that ancestor chain so it spans the full viewport.
  if (typeof document === 'undefined') return null;

  return createPortal(
    <div
      className={cn(
        'fixed inset-0 z-50 flex items-center justify-center',
        'bg-black/50 backdrop-blur-sm',
        overlayClassName
      )}
      onClick={handleBackdropClick}
    >
      {/* The dialog container's onClick only stops backdrop clicks from
          bubbling to the overlay close handler - it's intentional container
          behavior, not an interactive control. */}
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
      <div
        ref={contentRef}
        role='dialog'
        aria-modal='true'
        aria-labelledby={title ? titleId : undefined}
        aria-label={!title ? ariaLabel : undefined}
        className={cn(
          'relative mx-4 my-8 flex w-full flex-col',
          // A flex column, so the panel below can shrink to this cap. With a
          // plain block here its `h-full` resolved to the content height,
          // nothing ever scrolled, and a modal taller than 90vh was cut off.
          'max-h-[90vh] overflow-hidden',
          sizeClasses[size],
          className
        )}
        onClick={e => e.stopPropagation()}
      >
        <div
          className={cn(
            'flex h-full min-h-0 flex-col',
            'bg-card',
            'rounded-lg border shadow-xl',
            'border-border',
            contentClassName
          )}
        >
          {/* Header */}
          {(title || showCloseButton) && (
            <div
              className={cn(
                'flex items-center justify-between px-6 py-4',
                'border-b',
                'border-border'
              )}
            >
              {title && (
                <h2
                  id={titleId}
                  className={cn('text-lg font-semibold', 'text-foreground')}
                >
                  {title}
                </h2>
              )}
              {showCloseButton && (
                <button
                  // Icon-only, so without this it announces as an unnamed
                  // "button" — and it is the primary way out of a dialog that
                  // has no focus trap.
                  //
                  // `-m-1.5 p-3.5` below `sm`: a 44px touch target on a phone
                  // (the 16px icon plus 14px each side), with the negative
                  // margin cancelling the extra so the icon sits exactly where
                  // the 32px desktop button puts it.
                  aria-label='Close'
                  onClick={onClose}
                  className={cn(
                    '-m-1.5 rounded-lg p-3.5 transition-colors sm:m-0 sm:p-2',
                    'hover:bg-muted',
                    'hover:bg-muted'
                  )}
                >
                  <X className='h-4 w-4' />
                </button>
              )}
            </div>
          )}

          {/* Content */}
          <div className='flex-1 overflow-y-auto'>{children}</div>
        </div>
      </div>
    </div>,
    document.body
  );
}
